import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ScrollText, ArrowRight } from "lucide-react";
import { TERMS_PAGE } from "@/constants/testIds";
import { RESTAURANT } from "@/data/menu";
import Reveal from "@/components/site/Reveal";

const LAST_UPDATED = "25th June 2026";
const VERSION = "1.0.0";

const SECTIONS = [
  {
    id: "definitions",
    title: "1. Definitions",
    body: [
      { type: "p", text: "In these Terms, the following definitions apply:" },
      {
        type: "ul",
        items: [
          `"App" means the Giridhar Veg Restaurant Delivery App, accessible via mobile or web platforms.`,
          `"Restaurant" means Giridhar Veg Restaurant, a pure vegetarian restaurant established in 1991, located in Pune, Maharashtra, India.`,
          `"Customer" or "You" means any individual who accesses or uses the App to browse the menu or place an order.`,
          `"Order" means a request placed by a Customer via the App for delivery of food items offered by the Restaurant.`,
          `"Delivery Partner" means an in-house employee or staff of the Restaurant responsible for delivering Orders to Customers.`,
          `"Delivery Address" means the location specified by the Customer within a serviceable delivery zone at the time of placing an Order.`,
          `"Takeaway Order" means an Order placed through the App by a Customer who opts to collect the food in person directly from the Restaurant premises.`,
          `"Delivery Code" means a unique, one-time numeric or alphanumeric code generated by the App upon Order confirmation for Delivery Orders, which must be shared by the Customer with the Delivery Partner at the time of delivery to authenticate and complete the handover of the Order.`,
          `"Takeaway Code" means a unique, one-time numeric or alphanumeric code generated by the App upon Order confirmation for Takeaway Orders, which must be presented by the Customer to the Restaurant staff at the time of collection to authenticate and complete the handover of the Order.`,
          `"Restaurant Address" means ${RESTAURANT.address}.`,
          `"UPI" means Unified Payments Interface, the sole payment method accepted on the App.`,
          `"FSSAI" means the Food Safety and Standards Authority of India.`,
          `"Applicable Law" means all applicable laws, regulations, rules, and guidelines of India, including those of the State of Maharashtra and the city of Pune.`,
        ],
      },
    ],
  },
  {
    id: "about",
    title: "2. About the Restaurant",
    body: [
      { type: "p", text: `Giridhar Veg Restaurant is a 100% pure vegetarian restaurant established in 1991, located at ${RESTAURANT.address}. The Restaurant takes pride in serving authentic vegetarian food, prepared in a dedicated vegetarian kitchen with no use of meat, poultry, seafood, or eggs in any form.` },
      { type: "p", text: `The Restaurant holds all necessary licenses and registrations required under Applicable Law, including a valid FSSAI licence. FSSAI Licence No.: ${RESTAURANT.fssai}. GSTIN: ${RESTAURANT.gstin}.` },
    ],
  },
  {
    id: "eligibility",
    title: "3. Eligibility & Account Registration",
    body: [
      { type: "p", text: "To use the App, you must:" },
      {
        type: "ul",
        items: [
          "Be at least 18 years of age, or use the App under the supervision of a parent or legal guardian.",
          "Be legally capable of entering into a binding contract under the Indian Contract Act, 1872.",
          "Provide accurate, complete, and current information during registration, including your name, mobile number, and delivery address.",
          "Maintain the confidentiality of your account credentials. You are solely responsible for all activities under your account.",
          "Notify the Restaurant immediately of any unauthorised use of your account.",
        ],
      },
      { type: "p", text: "The Restaurant reserves the right to suspend or terminate accounts found to be providing false information or misusing the App." },
    ],
  },
  {
    id: "menu-pricing",
    title: "4. Menu, Food Items & Availability",
    body: [
      { type: "clause", number: "4.1", text: "The App displays the Restaurant's current menu, which is subject to change without prior notice. Prices displayed on the App are inclusive of applicable taxes unless stated otherwise." },
      { type: "clause", number: "4.2", text: "All items on the menu are 100% vegetarian. The Restaurant is a pure vegetarian establishment and does not prepare or serve any non-vegetarian food." },
      { type: "clause", number: "4.3", text: "Customers with food allergies, intolerances, or special dietary requirements are strongly advised to contact the Restaurant directly before placing an Order. While the Restaurant endeavours to provide accurate allergen information, it cannot guarantee a completely allergen-free environment." },
      { type: "clause", number: "4.4", text: "Menu availability may vary based on the time of day, season, or stock. Certain items may be unavailable at the time of ordering, in which case the Restaurant will notify the Customer at the earliest." },
    ],
  },
  {
    id: "orders",
    title: "5. Placing an Order",
    body: [
      { type: "clause", number: "5.1", text: "Customers may place Orders through the App during the Restaurant's operating hours: 9:00 AM to 11:00 PM (IST), every day, subject to any declared closures or public holidays." },
      { type: "clause", number: "5.2", text: "By placing an Order on the App, you represent and warrant that:" },
      {
        type: "ul",
        items: [
          "All information provided (delivery address, contact number) is accurate and complete.",
          "You are the authorised user of the UPI account used for payment.",
          "You will be available at the Delivery Address to receive the Order.",
        ],
      },
      { type: "clause", number: "5.3", text: "An Order shall be deemed confirmed only upon receipt of a confirmation notification from the App after successful payment. The Restaurant reserves the right to reject or cancel an Order in the event of unavailability of menu items, inability to deliver to the specified address, or any other operational reason. In such cases, a full refund will be initiated." },
      { type: "clause", number: "5.4", text: "Customers are requested to review their Order carefully before payment, as no modifications are permitted after an Order is confirmed." },
    ],
  },
  {
    id: "delivery",
    title: "6. Delivery Terms",
    body: [
      { type: "clause", number: "6.1", text: "Delivery is available only to selected locations within Pune, Maharashtra, as listed on the App. The Restaurant does not deliver outside the displayed serviceable zones. Customers are responsible for ensuring their Delivery Address falls within an available delivery zone before placing an Order." },
      { type: "clause", number: "6.2", text: "Delivery charges are calculated based on the delivery location and are displayed clearly in the App before Order confirmation. Delivery charges are non-refundable except in cases where the Order is cancelled by the Restaurant." },
      { type: "clause", number: "6.3", text: "There is no minimum order value for Takeaway Orders. However, a minimum order value of ₹250 applies to all Delivery Orders. Orders below this value will not be accepted for delivery." },
      { type: "clause", number: "6.4", text: "Estimated delivery times displayed on the App are indicative only and may vary due to traffic conditions, weather, high demand, or other operational circumstances. The Restaurant shall not be liable for delays caused by factors beyond its reasonable control." },
      { type: "clause", number: "6.5", text: "All deliveries are carried out exclusively by the Restaurant's own in-house Delivery Partners, who are trained to maintain food safety and hygiene standards during transit." },
      { type: "clause", number: "6.6", text: "Upon Order confirmation, the App will generate a unique Delivery Code. The Customer must share this code with the Delivery Partner at the time of delivery — the Order will not be handed over without it. Customers are advised to:" },
      {
        type: "ul",
        items: [
          "Keep the Delivery Code confidential and not share it with any person other than the Delivery Partner at the time of actual delivery.",
          "Not share the Delivery Code before the Delivery Partner has physically arrived with the Order.",
          "Contact the Restaurant immediately through the App's Help & Support if the Delivery Code is misplaced or compromised.",
        ],
      },
      { type: "clause", number: "6.7", text: "If a Customer is unavailable at the Delivery Address at the time of delivery and remains unreachable for a reasonable period, the Order may be marked as undeliverable. No refund shall be provided in such circumstances." },
    ],
  },
  {
    id: "takeaway",
    title: "7. Takeaway Service",
    body: [
      { type: "clause", number: "7.1", text: "The App offers a Takeaway option, allowing Customers to place an Order through the App and collect it in person directly from the Restaurant. To place a Takeaway Order, select the 'Takeaway' option on the App before checkout and complete payment via UPI." },
      { type: "clause", number: "7.2", text: `Customers must collect their Takeaway Order from the Restaurant premises at ${RESTAURANT.address}. Collection is available during operating hours: 9:00 AM to 11:00 PM (IST).` },
      { type: "clause", number: "7.3", text: "An estimated preparation time will be displayed on the App at the time of placing a Takeaway Order. Customers are advised to arrive at or after the indicated ready time. The Restaurant is not responsible for inconvenience caused by early arrival." },
      { type: "clause", number: "7.4", text: "Upon Order confirmation, the App will generate a unique Takeaway Code. Customers must present this code to Restaurant staff upon arrival — the Order will not be handed over without it. Customers are advised to:" },
      {
        type: "ul",
        items: [
          "Keep the Takeaway Code readily accessible on their App or notification at the time of collection.",
          "Not share the Takeaway Code with any third party, as it serves as the sole proof of identity for Order collection.",
          "Contact the Restaurant immediately through the App's Help & Support if the Takeaway Code is misplaced or compromised.",
        ],
      },
      { type: "clause", number: "7.5", text: "If a Customer fails to collect the Takeaway Order within 30 minutes of the indicated ready time without prior intimation to the Restaurant, the Order may be deemed abandoned and no refund shall apply." },
      { type: "clause", number: "7.6", text: "Takeaway Orders are not subject to any delivery charges. Only the food item cost and applicable taxes are charged." },
      { type: "clause", number: "7.7", text: "The same cancellation and refund policy as outlined in Clauses 9 and 10 applies to Takeaway Orders. Once a Takeaway Order is placed and payment is confirmed, it cannot be cancelled. Refund requests will be reviewed by the Restaurant on a case-by-case basis." },
    ],
  },
  {
    id: "payment",
    title: "8. Payment Terms",
    body: [
      { type: "clause", number: "8.1", text: "The App accepts payment exclusively through UPI (Unified Payments Interface). Cash on Delivery and card payments are not available." },
      { type: "clause", number: "8.2", text: "All transactions are processed in Indian Rupees (INR). Prices displayed include applicable Goods and Services Tax (GST) unless stated otherwise." },
      { type: "clause", number: "8.3", text: "Payment must be completed at the time of placing the Order. Orders will not be processed until payment is successfully confirmed." },
      { type: "clause", number: "8.4", text: "The Restaurant uses third-party UPI payment infrastructure. By making a payment, you also agree to the terms and conditions of the respective UPI service provider. The Restaurant shall not be liable for payment failures, delays, or errors caused by your UPI application, bank, or payment network." },
      { type: "clause", number: "8.5", text: "In the event of a double deduction or technical error resulting in incorrect payment, please contact us at the details provided in Clause 15. Verified erroneous payments will be refunded within 5–7 business days." },
      { type: "clause", number: "8.6", text: "All applicable taxes, including GST, are governed by the Goods and Services Tax Act, 2017. The Restaurant shall issue a digital receipt for each confirmed Order." },
    ],
  },
  {
    id: "cancellation",
    title: "9. Cancellation Policy",
    body: [
      { type: "clause", number: "9.1", text: "Once an Order is placed and payment is confirmed, the Order cannot be cancelled by the Customer. Customers are strongly advised to review their Order and delivery details carefully before completing payment." },
      { type: "clause", number: "9.2", text: "The Restaurant reserves the right to cancel an Order in the following circumstances:" },
      {
        type: "ul",
        items: [
          "The ordered item(s) are unavailable.",
          "The Delivery Address is outside the serviceable zone.",
          "Payment verification fails.",
          "Force majeure events, including but not limited to natural disasters, curfews, or government-imposed restrictions.",
        ],
      },
      { type: "clause", number: "9.3", text: "In the event of a Restaurant-initiated cancellation, the Customer shall receive a full refund of the amount paid, including delivery charges, within 5–7 business days." },
    ],
  },
  {
    id: "refunds",
    title: "10. Refund Policy",
    body: [
      { type: "clause", number: "10.1", text: "Refunds will be considered only in exceptional circumstances at the sole discretion of the Restaurant, including but not limited to:" },
      {
        type: "ul",
        items: [
          "Delivery of incorrect items that differ materially from the Order placed.",
          "Delivery of food that is of unacceptable quality (e.g., spoiled, foreign matter present).",
          "Non-delivery of the Order.",
          "Technical error resulting in duplicate payment.",
        ],
      },
      { type: "clause", number: "10.2", text: "To request a refund, the Customer must:" },
      {
        type: "ul",
        items: [
          `Contact the Restaurant within 24 to 48 hours of the scheduled delivery time at ${RESTAURANT.phone} or ${RESTAURANT.email}.`,
          "Provide the Order ID, a description of the issue, and, where applicable, photographic evidence.",
        ],
      },
      { type: "clause", number: "10.3", text: "The Restaurant will review all refund requests and communicate its decision to the Customer within 48 hours of receiving the complaint. The final decision on refunds rests solely with the Restaurant." },
      { type: "clause", number: "10.4", text: "Approved refunds will be credited to the originating UPI account within 5–7 business days, subject to the policies of the Customer's bank and UPI provider." },
      { type: "clause", number: "10.5", text: "The following circumstances are explicitly excluded from refund eligibility:" },
      {
        type: "ul",
        items: [
          "Change of mind after Order placement.",
          "Delays due to traffic, weather, or other factors beyond the Restaurant's control.",
          "Incorrect Delivery Address provided by the Customer.",
          "Customer unavailability at the Delivery Address during delivery.",
        ],
      },
    ],
  },
  {
    id: "food-safety",
    title: "11. Food Safety & Quality",
    body: [
      { type: "clause", number: "11.1", text: "The Restaurant is committed to maintaining the highest standards of food safety and hygiene in accordance with the Food Safety and Standards Act, 2006, and regulations issued by FSSAI." },
      { type: "clause", number: "11.2", text: "All food is prepared in a 100% vegetarian, hygienic kitchen. The Restaurant holds a valid FSSAI licence and complies with all applicable food safety regulations, including hygiene norms, correct labelling, and packaging requirements." },
      { type: "clause", number: "11.3", text: "Customers are advised to consume delivered food promptly upon receipt. The Restaurant shall not be responsible for any degradation in food quality due to delay in consumption after delivery." },
      { type: "clause", number: "11.4", text: "In case of any food safety concerns, Customers may also lodge a complaint directly with FSSAI through the Food Safety Connect helpline (1800-112-100)." },
    ],
  },
  {
    id: "conduct",
    title: "12. Customer Conduct & Responsibilities",
    body: [
      { type: "p", text: "By using the App, you agree to:" },
      {
        type: "ul",
        items: [
          "Use the App only for lawful purposes and in a manner that does not infringe the rights of others.",
          "Not engage in any abusive, threatening, or harassing behaviour toward the Restaurant staff or Delivery Partners.",
          "Not attempt to gain unauthorised access to the App or its underlying systems.",
          "Not use the App to place fraudulent or false Orders.",
          "Not resell food items purchased through the App.",
          "Comply with all Applicable Laws in connection with your use of the App.",
        ],
      },
      { type: "p", text: "The Restaurant reserves the right to suspend or permanently ban Customers who violate these conduct standards, without liability to the Customer." },
    ],
  },
  {
    id: "privacy",
    title: "13. Privacy & Data Protection",
    body: [
      { type: "clause", number: "13.1", text: "The Restaurant collects and processes personal data (including your name, mobile number, and delivery address) solely for the purpose of fulfilling your Order and improving service quality." },
      { type: "clause", number: "13.2", text: "Your personal data is governed by the Restaurant's Privacy Policy, available on the App. By using the App, you consent to the collection and processing of your personal data as described in the Privacy Policy." },
      { type: "clause", number: "13.3", text: "The Restaurant shall implement reasonable technical and organisational measures to protect your personal data in accordance with the Information Technology Act, 2000, the Information Technology (Amendment) Act, 2008, and the Digital Personal Data Protection Act, 2023." },
      { type: "clause", number: "13.4", text: "The Restaurant does not sell, rent, or share your personal data with third parties for marketing purposes. Data may be shared with payment processors or legal authorities as required by Applicable Law." },
    ],
  },
  {
    id: "ip",
    title: "14. Intellectual Property",
    body: [
      { type: "p", text: "All content on the App — including the Giridhar Veg Restaurant name, logo, menu photographs, text, and design elements — is the exclusive property of the Restaurant and is protected under the Trade Marks Act, 1999 and the Copyright Act, 1957." },
      { type: "p", text: "You may not reproduce, distribute, or commercially exploit any content from the App without the Restaurant's prior written consent." },
    ],
  },
  {
    id: "grievance",
    title: "15. Customer Grievance & Contact",
    body: [
      { type: "clause", number: "15.1", text: "If you have any concerns, complaints, or queries regarding your Order, refund, or these Terms, please contact us:" },
      {
        type: "ul",
        items: [
          `Name: Giridhar Veg Restaurant`,
          `Address: ${RESTAURANT.address}`,
          `Phone: ${RESTAURANT.phone} · Email: ${RESTAURANT.email}`,
          `App Support: Available within the App under 'Help & Support'`,
          `Operating Hours for Support: 9:00 AM – 11:00 PM (IST)`,
        ],
      },
      { type: "clause", number: "15.2", text: "In accordance with the Consumer Protection Act, 2019, Customers may also approach the appropriate Consumer Disputes Redressal Forum / Commission for unresolved disputes." },
    ],
  },
  {
    id: "liability",
    title: "16. Disclaimer of Warranties & Limitation of Liability",
    body: [
      { type: "clause", number: "16.1", text: "The App is provided on an 'as is' and 'as available' basis. The Restaurant makes no warranties, express or implied, regarding the uninterrupted or error-free operation of the App." },
      { type: "clause", number: "16.2", text: "The Restaurant shall not be liable for any indirect, incidental, or consequential damages arising from the use of the App, including loss of data, system failures, or interruptions caused by third-party infrastructure including UPI payment systems." },
      { type: "clause", number: "16.3", text: "The Restaurant's total liability to any Customer in connection with any Order shall not exceed the value of that Order." },
    ],
  },
  {
    id: "law",
    title: "17. Governing Law & Dispute Resolution",
    body: [
      { type: "clause", number: "17.1", text: "These Terms shall be governed by and construed in accordance with the laws of India." },
      { type: "clause", number: "17.2", text: "Any disputes arising out of or relating to these Terms or the use of the App shall first be attempted to be resolved amicably through direct communication with the Restaurant." },
      { type: "clause", number: "17.3", text: "If a dispute cannot be resolved amicably within 30 days, it shall be subject to the exclusive jurisdiction of the competent Courts in Pune, Maharashtra, India." },
      { type: "clause", number: "17.4", text: "Customers also retain the right to approach the District Consumer Disputes Redressal Commission, Pune, under the Consumer Protection Act, 2019." },
    ],
  },
  {
    id: "changes",
    title: "18. Amendments to These Terms",
    body: [
      { type: "p", text: `The Restaurant reserves the right to modify or update these Terms at any time. Updated Terms will be made available on the App with a revised effective date. Your continued use of the App after any such modification constitutes your acceptance of the updated Terms. Customers are encouraged to review these Terms periodically. Last updated: ${LAST_UPDATED}.` },
    ],
  },
  {
    id: "severability",
    title: "19. Severability & Waiver",
    body: [
      { type: "p", text: "If any provision of these Terms is found to be invalid, illegal, or unenforceable under Applicable Law, such provision shall be deemed modified to the minimum extent necessary to make it enforceable. The remaining provisions shall continue in full force and effect." },
      { type: "p", text: "A failure by the Restaurant to enforce any right under these Terms shall not constitute a waiver of that right." },
    ],
  },
  {
    id: "applicable-laws",
    title: "20. Applicable Laws & Regulatory Compliance",
    body: [
      { type: "p", text: "The Restaurant and this App operate in compliance with the following laws and regulations, among others:" },
      {
        type: "ul",
        items: [
          "Food Safety and Standards Act, 2006 (FSSAI)",
          "Consumer Protection Act, 2019",
          "Information Technology Act, 2000 & IT (Amendment) Act, 2008",
          "Digital Personal Data Protection Act, 2023",
          "Goods and Services Tax Act, 2017",
          "Indian Contract Act, 1872",
          "Sale of Goods Act, 1930",
          "Trade Marks Act, 1999",
          "Maharashtra Shops and Establishments (Regulation of Employment and Conditions of Service) Act, 2017",
          "Payment and Settlement Systems Act, 2007 (governing UPI transactions)",
        ],
      },
    ],
  },
  {
    id: "acceptance",
    title: "21. Acceptance of Terms",
    body: [
      { type: "p", text: "By registering on, accessing, or using the Giridhar Veg Restaurant Delivery App, you confirm that you have read, understood, and agreed to these Terms and Conditions in their entirety. These Terms constitute a legally binding agreement between you and Giridhar Veg Restaurant." },
    ],
  },
];

export default function TermsPage() {
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observers = new Map();

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
      );

      observer.observe(el);
      observers.set(id, observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  return (
    <div data-testid={TERMS_PAGE.page}>
      {/* HEADER */}
      <section className="bg-cream-deep/50 border-b border-borderc/60 mt-24">
        <div className="container-x pt-16 sm:pt-20 pb-14">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 text-brand text-[0.7rem] uppercase tracking-[0.2em]">
              <ScrollText className="w-3.5 h-3.5" /> Legal
            </span>
            <h1 className="mt-5 font-serif text-5xl sm:text-6xl text-ink leading-[1.05] max-w-3xl text-balance">
              Terms &amp; Conditions
            </h1>
            <p className="mt-5 text-ink-muted max-w-2xl leading-relaxed">
              Please read these Terms and Conditions ("Terms") carefully before using the Giridhar Veg Restaurant Delivery App ("App") operated by Giridhar Veg Restaurant. By downloading, accessing, or using the App to place an order, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, please discontinue use of the App immediately.
            </p>
            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-ink-muted">
              Last updated: {LAST_UPDATED}
            </p>
            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-ink-muted">
              Version: {VERSION}
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTENT */}
      <section className="container-x py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Table of contents */}
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs uppercase tracking-[0.18em] text-brand font-medium mb-4">
                On this page
              </p>
              <nav className="rounded-2xl bg-white border border-borderc/60 p-5 shadow-sm">
                <ol className="space-y-1 text-sm">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      
                        <a href={`#${s.id}`}
                        className={`block leading-snug px-3 py-1.5 rounded-lg transition-colors ${
                          activeId === s.id
                            ? "bg-brand/10 text-brand font-medium"
                            : "text-ink-muted hover:text-brand"
                        }`}
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
              <Link
                to="/contact-us"
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-brand hover:text-brand-hover font-medium"
              >
                Questions? Contact us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>

          {/* Sections */}
          <article className="lg:col-span-8 xl:col-span-9 space-y-12">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.id} delay={i * 40}>
                <section
                  id={s.id}
                  data-testid={TERMS_PAGE.section}
                  className="scroll-mt-28"
                >
                  <h2 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">
                    {s.title}
                  </h2>
                  <div className="hairline mt-4 mb-5 w-16" />
                  <div className="space-y-4 text-ink-muted leading-relaxed">
                    {s.body.map((block, idx) => {
                      if (block.type === "ul") {
                        return (
                          <ul key={idx} className="list-disc list-outside pl-10 space-y-1.5">
                            {block.items.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        );
                      }
                      if (block.type === "clause") {
                        return (
                          <p key={idx}>
                            <span className="font-medium text-ink mr-2">{block.number}</span>
                            {block.text}
                          </p>
                        );
                      }
                      return <p key={idx}>{block.text}</p>;
                    })}
                  </div>
                </section>
              </Reveal>
            ))}
          </article>
        </div>
      </section>
    </div>
  );
}
